/**
 * Constants for arrow calculations
 *
 * This file contains all magic numbers extracted from the original implementation
 * with descriptive names and documentation explaining their purpose.
 */

/**
 * Lone Pair positioning constants
 */
export const LP_CONSTANTS = {
  /**
   * Additional offset added to the base lone pair offset when calculating
   * the arrow start point from a lone pair (helper.ts:36)
   */
  START_POINT_OFFSET_ADDITION: 0.8,

  /**
   * Threshold for determining if a direction component is "diagonal" or "near axis"
   * Used in overlap avoidance logic (EndPointMath.ts:115-118)
   * Values greater than -0.9 are considered "not strongly negative"
   */
  DIRECTION_THRESHOLD: -0.9,

  /**
   * Horizontal distance threshold for special case handling
   * When atom-to-destination distance exceeds this, different overlap logic applies
   * (EndPointMath.ts:120)
   */
  HORIZONTAL_DISTANCE_THRESHOLD: -10,
} as const;

/**
 * Direction vectors commonly used for overlap avoidance
 * These represent cardinal and ordinal directions in 2D space
 */
export const DIRECTION_VECTORS = {
  /** Right-up diagonal */
  RIGHT_UP: [1, 1, 0],
  /** Left-up diagonal */
  LEFT_UP: [-1, 1, 0],
  /** Down (negative Y) */
  DOWN: [0, -1, 0],
  /** Up (positive Y) */
  UP: [0, 1, 0],
  /** Left (negative X) */
  LEFT: [-1, 0, 0],
  /** Right (positive X) */
  RIGHT: [1, 0, 0],
} as const;

/**
 * Radical arrow positioning constants
 */
export const RADICAL_CONSTANTS = {
  /**
   * Scalar multiplier for perpendicular offset when positioning radical arrows
   * Applied to format.atom.radius * 2 (EndPointMath.ts:100)
   */
  PERPENDICULAR_OFFSET_SCALAR: 2 / 3,
} as const;

/**
 * Bond arrow positioning constants
 */
export const BOND_CONSTANTS = {
  /**
   * Perpendicular offset multiplier for bond direction calculations
   * Used to determine which side of a bond to place the arrow (EndPointMath.ts:290)
   */
  PERPENDICULAR_OFFSET: 1.2,

  /**
   * End point extension distance for bond endpoints
   * Extends the arrow end point beyond the bond (EndPointMath.ts:241)
   */
  END_POINT_EXTENSION: 1.2,
} as const;

/**
 * Curve control point constants
 */
export const CURVE_CONSTANTS = {
  /**
   * Distance multiplier for control point offset from start position
   * (CurveMath.ts:11)
   */
  START_CONTROL_POINT_DISTANCE: 8,

  /**
   * Scalar for control point calculation
   * Applied to direction vector (CurveMath.ts:45)
   */
  CONTROL_POINT_SCALAR: 0.5,
} as const;

/**
 * Arrow head positioning constants
 */
export const ARROW_HEAD_CONSTANTS = {
  /**
   * Distance to offset arrow head from end point when destination is adjacent
   * (CurveMath.ts:50)
   */
  ADJACENT_HEAD_OFFSET: -1.2,

  /**
   * Distance to offset arrow head from end point when destination is not adjacent
   * (CurveMath.ts:50)
   */
  NON_ADJACENT_HEAD_OFFSET: -0.8,

  /**
   * Perpendicular offset for arrow head side calculation
   * (CurveMath.ts:24-25)
   */
  SIDE_CALCULATION_OFFSET: 1,
} as const;

/**
 * General arrow endpoint constants
 */
export const ENDPOINT_CONSTANTS = {
  /**
   * Generic offset for particle endpoints when offset is not used
   * (EndPointMath.ts:250)
   */
  PARTICLE_GENERIC_OFFSET: 1.1,

  /**
   * Blending weight for combining start and end directions
   * (EndPointMath.ts:40, 43)
   */
  DIRECTION_BLEND_WEIGHT: 0.5,
} as const;

/**
 * Type guards for direction checking
 * These encapsulate common direction range checks
 */
export const isDirectionInRange = (
  value: number,
  min: number,
  max: number
): boolean => {
  return value > min && value < max;
};

/**
 * Checks if a direction is diagonal down-left
 * (corresponds to EndPointMath.ts:114-119)
 */
export const isDownLeftDiagonal = (direction: number[]): boolean => {
  return (
    isDirectionInRange(direction[0], LP_CONSTANTS.DIRECTION_THRESHOLD, 0) &&
    isDirectionInRange(direction[1], LP_CONSTANTS.DIRECTION_THRESHOLD, 0)
  );
};

/**
 * Checks if a direction is diagonal up-left
 * (corresponds to EndPointMath.ts:128-133)
 */
export const isUpLeftDiagonal = (direction: number[]): boolean => {
  return (
    isDirectionInRange(direction[0], LP_CONSTANTS.DIRECTION_THRESHOLD, 0) &&
    isDirectionInRange(direction[1], 0, 1)
  );
};

/**
 * Checks if a direction points right and down
 * (corresponds to EndPointMath.ts:143)
 */
export const isRightDown = (direction: number[]): boolean => {
  return direction[1] < 0 && direction[0] > 0;
};

/**
 * Checks if a direction points left and up
 * (corresponds to EndPointMath.ts:153)
 */
export const isLeftUp = (direction: number[]): boolean => {
  return direction[1] > 0 && direction[0] < 0;
};

/**
 * Checks if a direction points up-right
 * (corresponds to EndPointMath.ts:163)
 */
export const isUpRight = (direction: number[]): boolean => {
  return direction[1] > 0 && direction[0] > 0;
};

/**
 * Checks if a direction points down-left
 * (corresponds to EndPointMath.ts:174)
 */
export const isDownLeft = (direction: number[]): boolean => {
  return direction[1] < 0 && direction[0] < 0;
};
